/**
 * DistributionPreviewTable - Reusable recipient table for preview + history
 */

import React, { useState } from 'react';
import { Badge } from '../../ui/components/Badge';
import { FeatherCopy, FeatherCheck } from '@subframe/core';
import { useDistributionRecipients } from '../../hooks/useDistribution';

interface DistributionPreviewTableProps {
  projectSlug: string;
  periodId: string;
  showExcluded?: boolean;
}

const PAGE_SIZE = 15;

export const DistributionPreviewTable: React.FC<DistributionPreviewTableProps> = ({
  projectSlug,
  periodId,
  showExcluded = false,
}) => {
  const [page, setPage] = useState(0);
  const { data, isLoading } = useDistributionRecipients(projectSlug, periodId, {
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    includeExcluded: showExcluded,
  });

  if (isLoading) {
    return <div className="text-subtext-color py-2">Loading recipients...</div>;
  }

  if (!data || data.recipients.length === 0) {
    return <div className="text-subtext-color py-2">No recipients for this period.</div>;
  }

  const totalPages = Math.ceil(data.total / PAGE_SIZE);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-caption-bold font-caption-bold text-subtext-color">
        Recipients ({data.total})
      </span>
      <div className="overflow-x-auto">
        <table className="w-full text-body font-body">
          <thead>
            <tr className="border-b border-neutral-border text-left text-caption font-caption text-subtext-color">
              <th className="px-2 py-1">XRPL Address</th>
              <th className="px-2 py-1">Reward Address</th>
              <th className="px-2 py-1 text-right">Points</th>
              <th className="px-2 py-1 text-right">Payout</th>
              <th className="px-2 py-1">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.recipients.map(r => (
              <tr key={r.id} className="border-b border-neutral-border">
                <td className="px-2 py-1 text-caption font-mono">
                  <CopyAddr address={r.xrpl_address} />
                </td>
                <td className="px-2 py-1 text-caption font-mono text-default-font">
                  {r.reward_address ? (
                    <CopyAddr address={r.reward_address} start={10} />
                  ) : (
                    <span className="text-subtext-color">-</span>
                  )}
                </td>
                <td className="px-2 py-1 text-right text-default-font">
                  {Number(r.points).toLocaleString()}
                </td>
                <td className="px-2 py-1 text-right text-default-font">
                  {Number(r.payout_amount).toLocaleString(undefined, { maximumFractionDigits: 6 })}
                </td>
                <td className="px-2 py-1">
                  {r.is_excluded ? (
                    <Badge variant="error">Excluded</Badge>
                  ) : r.is_unregistered ? (
                    <Badge variant="warning">Unregistered</Badge>
                  ) : (
                    <Badge variant="success">OK</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-1">
          <button
            className="text-caption font-caption text-brand-600 hover:text-brand-700 disabled:text-neutral-400 disabled:cursor-default"
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </button>
          <span className="text-caption font-caption text-subtext-color">
            {page + 1} / {totalPages}
          </span>
          <button
            className="text-caption font-caption text-brand-600 hover:text-brand-700 disabled:text-neutral-400 disabled:cursor-default"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const CopyAddr: React.FC<{ address: string; start?: number; end?: number }> = ({
  address,
  start = 6,
  end = 4,
}) => {
  const [copied, setCopied] = useState(false);
  const truncated = `${address.slice(0, start)}...${address.slice(-end)}`;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <span className="inline-flex items-center gap-1 text-default-font">
      {truncated}
      <button onClick={handleCopy} className="p-0.5 rounded hover:bg-neutral-100 transition-colors">
        {copied ? (
          <FeatherCheck className="w-3.5 h-3.5 text-green-500" />
        ) : (
          <FeatherCopy className="w-3.5 h-3.5 text-subtext-color hover:text-default-font" />
        )}
      </button>
    </span>
  );
};
